import React, { useEffect, useState } from 'react';
import { ThemeContext, ACCENTS, SKINS, type Density, type SkinId } from './theme';

type Theme = 'light' | 'dark';

interface ThemeProviderProps {
  children: React.ReactNode;
}

// Preferências do shell Íris — tema + densidade + cor de destaque, persistidas
// juntas (chave do handoff design_handoff_navigation).
const PREFS_KEY = 'utilyhub_iris_prefs';

interface IrisPrefs {
  theme?: Theme;
  density?: Density;
  accent?: string;
  skin?: SkinId;
}

function lerPrefs(): IrisPrefs {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as IrisPrefs;
  } catch {
    return {};
  }
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const prefs = lerPrefs();
    if (prefs.theme === 'light' || prefs.theme === 'dark') return prefs.theme;
    // Fallback na chave legada 'theme' (instalações existentes) e na preferência do SO.
    const legado = localStorage.getItem('theme') as Theme | null;
    if (legado === 'light' || legado === 'dark') return legado;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [density, setDensity] = useState<Density>(() => {
    const d = lerPrefs().density;
    return d === 'compact' ? 'compact' : 'comfortable';
  });
  const [accent, setAccent] = useState<string>(() => lerPrefs().accent ?? ACCENTS[0].hex);
  const [skin, setSkin] = useState<SkinId>(() => {
    const s = lerPrefs().skin;
    return SKINS.some((k) => k.id === s) ? (s as SkinId) : 'claro';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    // Classe legada — TODAS as telas existentes usam Tailwind `dark:`.
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    // Atributos/vars Íris — o shell novo (sidebar/topbar/palette) usa os tokens.
    root.setAttribute('data-theme', theme);
    root.setAttribute('data-density', density);
    root.setAttribute('data-skin', skin);
    root.style.setProperty('--accent', accent);
    // Persiste o conjunto + mantém a chave legada em sincronia.
    localStorage.setItem(PREFS_KEY, JSON.stringify({ theme, density, accent, skin }));
    localStorage.setItem('theme', theme);
  }, [theme, density, accent, skin]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, density, setDensity, accent, setAccent, skin, setSkin }}>
      {children}
    </ThemeContext.Provider>
  );
}
